import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, Sprout, BarChart3, Package } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

// 🎄 CHRISTMAS THEME TOGGLE 🎄
// To switch themes, comment/uncomment ONE of the CSS imports below:
// import '../CSS/Login.christmas.css';  // ← Christmas theme (currently active)
import '../CSS/Login.css';          // ← Normal theme (uncomment to use)

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const email = watch('email');
  const password = watch('password');

  const onSubmit = async (data) => {
    setIsLoading(true);
    
    const result = await login(data.email, data.password);
    
    if (result.success) {
      toast.success('Welcome back! Redirecting to menu...');
      setTimeout(() => navigate('/menu'), 1000);
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="login-container">
      {/* Left Panel - Branding */}
      <div className="login-left-panel">
        <div className="plant-icon-large">
          <Sprout size={80} />
        </div>
        <h1 className="brand-title">Urvann BloomIQ</h1>
        <p className="brand-tagline">Cultivating Success, One Plant at a Time</p>
        
        <div className="features-box">
          <div className="feature-item">
            <Sprout size={20} />
            <span>Real-time Analytics</span>
          </div>
          <div className="feature-item">
            <BarChart3 size={20} />
            <span>Growth Tracking</span>
          </div>
          <div className="feature-item">
            <Package size={20} />
            <span>Inventory Management</span>
          </div>
        </div>

        <div className="plant-icon-small">
          <Sprout size={40} />
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="login-right-panel">
        <div className="login-form-wrapper">
          <div className="login-form-container">
            <div className="form-header">
              <h2 className="welcome-heading">Welcome Back</h2>
              <p className="welcome-subtitle">Sign in to access your marketing dashboard</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="login-form">
              {/* Email Field */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <div className="input-wrapper">
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="Enter your email"
                  />
                </div>
                <AnimatePresence>
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="error-message"
                    >
                      {errors.email.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Password Field */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    className={`form-input ${errors.password ? 'error' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <AnimatePresence>
                  {errors.password && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="error-message"
                    >
                      {errors.password.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Remember Me Checkbox */}
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="rememberMe"
                  {...register('rememberMe')}
                />
                <label htmlFor="rememberMe">Remember me</label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="login-button"
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner"></div>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <span>Login</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            {/* Contact Admin Link */}
            <div className="form-footer">
              <p className="admin-link">
                Need access? <Link to="#" className="admin-link-text">Contact Administrator</Link>
              </p>

              {/* Terms and Privacy */}
              <p className="terms-text">
                By signing in, you agree to our{' '}
                <Link to="#" className="terms-link">Terms of Service</Link> and{' '}
                <Link to="#" className="terms-link">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
 